import { Fragment, useCallback, useContext, useEffect, useState } from "react";

import { ApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { useDialog } from "@datanac/datanac-mui-toolkit/dist/hooks";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import { Button, Dialog, Divider, FormControl, Grid, IconButton, InputAdornment, Typography } from "@mui/material";
import { _sessionManager } from "api/ApiHelper";
import { AppContext } from "App";
import { StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import { MODULE_MARKETING } from "components/Menu/config/constants";
import { useSnackbar } from "notistack";
import { v4 } from "uuid";

import { ClientAccountNumberDialog } from "./ClientAccountNumberDialog";

export const ClientAccountsList = ({ member, onChange }) => {
  const { globalState } = useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();
  const { component: confirmDialogComponent, actions: confirmDialogActions } = useDialog();

  const [isLoading, setIsLoading] = useState(false);
  const [dataDictionary, setDataDictionary] = useState([]);
  const [editingItem, setEditingItem] = useState(null);

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const _loadDataDictionary = useCallback(async () => {
    try {
      if (member?.producer_token) {
        setIsLoading(true);
        const _filter = {
          producer_token: member?.producer_token,
          is_active: true,
        };

        const api = new ApiHelper({
          baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
          config: {},
          sessionManager: _sessionManager,
          _instance: null,
        });

        const _dataDictionary = await api.callPost("DataDictionary/Search", _filter, globalState);
        setDataDictionary(_dataDictionary);
        setIsLoading(false);
      }
    } catch (err) {
      handleError(err);
      setIsLoading(false);
    }
  }, [member?.producer_token, globalState]);

  useEffect(() => {
    _loadDataDictionary();
  }, [_loadDataDictionary]);

  const handleChangeDataDictionary = async (account) => {
    try {
      const _newState = { ...account };
      const SECTION_TILLEY = "Tilley";
      // Create ID if not exists
      _newState["id"] = _newState["id"] || v4();
      _newState["producer_token"] = member?.producer_token;
      _newState["section"] = SECTION_TILLEY;
      _newState["module"] = MODULE_MARKETING;
      _newState["object"] = account.object;
      _newState["container"] = account.container;
      _newState["value"] = account.value;

      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        // No caching:
        _instance: null,
      });
      await api.callPost("marketing/DataDictionary", _newState, globalState);

      _loadDataDictionary();
    } catch (err) {
      handleError(err);
    }
  };

  const handleRowDelete_Account_Intern = async (row) => {
    const _newRow = { ...row };
    _newRow.is_active = false;

    // HandleChange will reload data
    handleChangeDataDictionary(_newRow);
  };

  const handleRowDelete_Account = useCallback(
    async (row) => {
      confirmDialogActions.confirm({
        title: "Delete this entry?",
        prompt: "Are you sure you want to delete this entry?",
        promptDetail: null,
        callback: () => {
          handleRowDelete_Account_Intern(row);
        },
      });
    },
    [confirmDialogActions]
  );

  if (!member?.producer_token) {
    return null;
  }

  return (
    <>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Accounts
      </Typography>

      <Grid container spacing={2}>
        {(dataDictionary || [])?.map((data, index) => (
          <Fragment key={index}>
            {data.object === "office_code" && (
              <Grid item xs={12} md={6} key={index}>
                <FormControl fullWidth={true}>
                  <StatefulControlledTextField
                    label={"Office Code (" + data?.container + ")"}
                    name="office_code"
                    isRequired={true}
                    defaultValue={data?.value || ""}
                    onChange={onChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </FormControl>
              </Grid>
            )}

            {data?.object === "account_number" && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth={true}>
                  <StatefulControlledTextField
                    label={"Account Number (" + data?.container + ")"}
                    name="account_number"
                    isRequired={true}
                    defaultValue={data?.value || ""}
                    variant="outlined"
                    onChange={onChange}
                    InputLabelProps={{ shrink: true }}
                    endAdornment={
                      <InputAdornment position="end" className="delete">
                        <IconButton onClick={() => handleRowDelete_Account(data)} edge="end" color="secondary">
                          <DeleteOutlined />
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>
            )}
          </Fragment>
        ))}

        <Grid item xs={12} md={6} style={{ paddingTop: "16px" }}>
          <Typography className="addAccountCustom" variant="body2" sx={{ mb: 1 }}>
            Add Account
          </Typography>
          <Button
            fullWidth
            sx={{ borderRadius: 1 }}
            variant="outlined"
            color="secondary"
            onClick={() => setEditingItem({})}
          >
            <AddCircleOutline />
          </Button>
        </Grid>
      </Grid>

      <Divider sx={{ mt: 2 }} />

      <Dialog open={Boolean(editingItem != null)}>
        <ClientAccountNumberDialog
          data={editingItem}
          onChange={handleChangeDataDictionary}
          onClose={() => setEditingItem(null)}
        />
      </Dialog>
      {confirmDialogComponent}
    </>
  );
};
